import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { darkModeAware, withStyles } from '@glu/theming';
import { withInputAPI } from '@glu/validation-react';
import Error from '../Error/Error';
import LabelText from '../LabelText/LabelText';
import OptionalInlineText from '../OptionalInlineText/OptionalInlineText';
import SelectBase from './SelectBase';
import styles from './Select.styles';

let uniqueId = 0;

const Select = ({
  classes,
  className,
  dark,
  error,
  errors,
  htmlId,
  labelSpanClass,
  labelText,
  optional,
  options,
  placeholderText,
  screenReaderLabel,
  selectClass,
  inline,
  required,
  warning,
  ...props
}) => {
  const errorMessage = error || errors?.[0];

  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => htmlId || `select-${uniqueId++}`, [htmlId]);
  return (
    /** Note label-has-for was depreciated for label-has-associated-control
     * label-has-for doesn't support controlComponents config which lets you specify
     * that SelectBase is the control component for the label below. The rule has been disabled
     * for now
     */
    // eslint-disable-next-line jsx-a11y/label-has-for
    <label htmlFor={id} className={`${className} ${classes.root}`}>
      <LabelText
        className={labelSpanClass}
        dark={dark}
        hasError={!!errorMessage}
        label={labelText}
        optional={optional}
        screenReaderLabel={screenReaderLabel}
        inline={inline}
        warning={warning}
        {...props}
      />
      <div className={classes.wrap}>
        <SelectBase
          className={selectClass}
          dark={dark}
          error={errorMessage}
          firstOption={placeholderText ? <option value="" disabled={required}>{placeholderText}</option> : null}
          htmlId={id}
          required={required}
          warning={warning}
          {...props}
        >
          {options.map(option => {
            const { label, ...restOption } = option;
            return (
              <option key={`${id}-${option.value}`} {...restOption}>
                {label}
              </option>
            );
          })}
        </SelectBase>

        <Error error={errorMessage} warning={warning} dark={dark} />
      </div>
      {
        optional && inline
          ? <OptionalInlineText hasError={!!errorMessage} warning={warning} />
          : null
      }
    </label>
  );
};

Select.propTypes = {
  /** Id for the input if a randomly generated id isn't desired */
  htmlId: PropTypes.string,
  /** Text for the span inside the label */
  labelText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  /** Options for the select */
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  /** Placeholder text for the select */
  placeholderText: PropTypes.string,
  /** Boolean to indicate whether or not you want the standard inline layout */
  inline: PropTypes.bool,
  /** Classes provided by the theme */
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  /** Custom classes applied to label wrapping component */
  className: PropTypes.string,
  /** Custom class applied to label span element */
  labelSpanClass: PropTypes.string,
  /** Hides the label from sighted users */
  screenReaderLabel: PropTypes.bool,
  /** Custom classes applied to select element */
  selectClass: PropTypes.string,
  /** The error to display */
  error: PropTypes.string,
  /** Use warning styles if true */
  warning: PropTypes.bool,
  /** Full errors onject. */
  errors: PropTypes.arrayOf(PropTypes.string),
  /** dark mode boolean */
  dark: PropTypes.bool,
  /** boolean to indicate if select is required */
  required: PropTypes.bool,
  /** Shows optional text on label */
  optional: PropTypes.bool
};
Select.defaultProps = {
  className: '',
  htmlId: '',
  inline: false,
  labelSpanClass: '',
  screenReaderLabel: false,
  placeholderText: '',
  selectClass: '',
  error: '',
  warning: false,
  errors: undefined,
  dark: false,
  required: false,
  optional: false
};

const StyledSelect = darkModeAware(withStyles(styles)(Select));
export { Select as BareSelect };
const WithInputSelect = withInputAPI(StyledSelect);
WithInputSelect.displayName = 'Select';
export default WithInputSelect;
export const ValidatedSelect = WithInputSelect;
